import Cookies from 'js-cookie';
import axios from 'axios';
import { consumeReturnPath } from './utils/returnUrl';
import { resolveLandingPage } from './utils/pageRoutes';

/**
 * Applies server login payload to cookies (same shape as POST /login and /signup/verify).
 * When needs_store_setup is true, sets needs_store_setup cookie and navigates to /setup-store.
 */
export function applyLoginPayloadToCookies(data, navigate) {
  const d = data?.data || {};
  const shopVal = d.shop || '';
  const shopCookie = shopVal ? String(shopVal).replace('.myshopify.com', '') : '';

  // const baseCookieOpts = {
  //   sameSite: 'strict',
  //   secure: window.location.protocol === 'https:',
  //   expires: 0.5,
  // };
  const baseCookieOpts = {
    sameSite: process.env.REACT_APP_WENV !== 'local' ? 'strict' : '',
    secure: process.env.REACT_APP_WENV !== 'local',
    domain: process.env.REACT_APP_WENV !== 'local' ? '.cognitiveux.ai' : '',
    path: '/',
    expires: 0.5,
  };

  Cookies.set('stores', d.stores, baseCookieOpts);
  Cookies.set('allowed_pages_json', d.allowed_pages_json, baseCookieOpts);
  Cookies.set('shop_name', d.shop_name || '', baseCookieOpts);
  Cookies.set('shop', shopCookie, baseCookieOpts);
  Cookies.set('show_under_dev_pages', d.show_under_dev_pages, baseCookieOpts);
  Cookies.set('user_email', d.user_email, baseCookieOpts);
  Cookies.set('user_role', d.user_role, baseCookieOpts);
  Cookies.set('user_permissions', d.user_permissions, baseCookieOpts);

  const devPermissionsStr = d.dev_page_permissions_by_store;
  const devPermissions =
    devPermissionsStr != null
      ? typeof devPermissionsStr === 'string'
        ? devPermissionsStr
        : JSON.stringify(devPermissionsStr)
      : '{}';
  Cookies.set('dev_page_permissions_by_store', devPermissions, {
    sameSite: process.env.REACT_APP_WENV !== 'local' ? 'strict' : '',
    secure: process.env.REACT_APP_WENV !== 'local',
    domain: process.env.REACT_APP_WENV !== 'local' ? '.cognitiveux.ai' : '',
    path: '/',
    expires: 0.5,
  });

  if (d.needs_store_setup) {
    Cookies.set('needs_store_setup', 'true', baseCookieOpts);
  } else {
    Cookies.remove('needs_store_setup', {
      secure: process.env.REACT_APP_WENV !== "local",
      domain: process.env.REACT_APP_WENV !== "local" ? ".cognitiveux.ai" : "",
      path: "/",
    });
  }

  if (navigate) {
    // Store setup is a hard gate — it outranks wherever the user was going.
    const returnPath = d.needs_store_setup ? null : consumeReturnPath();
    if (returnPath) {
      // replace() navigates and loads in one step. navigate()+reload() races:
      // the reload can fire before the router has flushed the new URL, which
      // would reload the *old* location and drop the return path.
      window.location.replace(returnPath);
      return;
    }

    if (d.needs_store_setup) {
      navigate('/setup-store', { replace: true });
    } else {
      // Land on a page this user's store actually grants (Campaign Summary →
      // Agents → first accessible), from the payload itself — the cookies were
      // written above but may not read back synchronously in every environment.
      let landing = '/campaign-summary';
      try {
        const pagesByShop = typeof d.allowed_pages_json === 'string'
          ? JSON.parse(d.allowed_pages_json || '{}')
          : (d.allowed_pages_json || {});
        landing = resolveLandingPage(pagesByShop[shopCookie]);
      } catch {
        // Malformed payload — keep the historical default.
      }
      navigate(landing, { replace: true });
    }
    window.location.reload();
  }
}

/**
 * Fetches the latest allowed_pages for shopDomain from the server, merges it
 * into the existing allowed_pages_json cookie, and reloads the page so the
 * sidebar and route guards reflect the change immediately.
 *
 * Call this after any subscription event that changes feature access:
 *   - subscribe / upgrade confirm / cancel
 */
export async function refreshAllowedPagesCookie(appUrl, shopDomain) {
  await syncAllowedPagesCookie(appUrl, shopDomain);
  // Reload so App.js re-reads the cookie and re-renders with new access
  window.location.reload();
}

/**
 * The same refresh WITHOUT the reload, for callers that are mid-flow and will
 * navigate themselves — the account-setup wizard would otherwise reload out of
 * step 3 the moment a plan activated.
 */
export async function syncAllowedPagesCookie(appUrl, shopDomain) {
  try {
    const shopKey = (shopDomain || '').replace('.myshopify.com', '');
    if (!shopKey) return;

    const response = await axios.get(
      `${appUrl}/api/subscriptions/refresh-allowed-pages`,
      { params: { shop_domain: shopKey }, withCredentials: true }
    );

    if (!response.data?.success) return;

    const freshMap = response.data.data?.allowed_pages_json || {};

    // Merge into the existing cookie (other shops' data is preserved)
    const existing = (() => {
      try { return JSON.parse(Cookies.get('allowed_pages_json') || '{}'); }
      catch { return {}; }
    })();

    const merged = { ...existing, ...freshMap };

    Cookies.set('allowed_pages_json', JSON.stringify(merged), {
      sameSite: process.env.REACT_APP_WENV !== 'local' ? 'strict' : '',
      secure: process.env.REACT_APP_WENV !== 'local',
      domain: process.env.REACT_APP_WENV !== 'local' ? '.cognitiveux.ai' : '',
      path: '/',
      expires: 0.5,
    });

  } catch (err) {
    console.error('Failed to refresh allowed_pages_json cookie', err);
  }
}
